'use client';

import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { openExternalCheckout } from '@/app/_module/utils/externalCheckout';
import { Monitor, CreditCard, Phone, Globe, UserPlus } from 'lucide-react';

export type WebVersionFeature = 'subscription' | 'area-code' | 'payment' | 'billing' | 'signup';

interface WebVersionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: WebVersionFeature;
}

const featureConfig: Record<WebVersionFeature, {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  details: string[];
}> = {
  subscription: {
    title: 'Account Management',
    description: 'Account and plan management is available on the web version of InsuraCentral.',
    icon: CreditCard,
    details: [
      'View and manage your account',
      'Access account settings',
      'Review account history',
    ],
  },
  'area-code': {
    title: 'Phone Number Management',
    description: 'Phone number setup is available on the web version of InsuraCentral.',
    icon: Phone,
    details: [
      'Browse available area codes',
      'Set up new phone numbers',
      'Manage existing numbers',
      'Configure call routing',
    ],
  },
  payment: {
    title: 'Account Settings',
    description: 'Account settings are available on the web version of InsuraCentral.',
    icon: CreditCard,
    details: [
      'Manage account settings',
      'Update account information',
      'View account activity',
    ],
  },
  billing: {
    title: 'Account History',
    description: 'Account history is available on the web version of InsuraCentral.',
    icon: Globe,
    details: [
      'Download documents',
      'View account history',
      'Update account details',
    ],
  },
  signup: {
    title: 'Create an Account',
    description: 'Account registration is available on the web version of InsuraCentral.',
    icon: UserPlus,
    details: [
      'Create your InsuraCentral account',
      'Set up your agency profile',
      'Choose your subscription plan',
      'Complete onboarding steps',
    ],
  },
};

export function WebVersionModal({ open, onOpenChange, feature }: WebVersionModalProps) {
  const config = featureConfig[feature];
  const Icon = config.icon;
  const [mounted, setMounted] = useState(false);

  // Ensure we're on the client before using portal
  // This effect MUST run regardless of open state
  // This effect MUST run regardless of open state, so the mounted check comes
  // before the open check rather than being combined with it.
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;
  if (!open) return null;

  const modalContent = (
    <>
      {/* Custom overlay with higher z-index for mobile */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 99998,
        }}
        onClick={() => onOpenChange(false)}
      />
      {/* Custom centered modal for mobile compatibility */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 99999,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '400px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            padding: '24px',
            maxHeight: '85vh',
            overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={() => onOpenChange(false)}
            style={{
              position: 'absolute',
              right: '16px',
              top: '16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <span className="sr-only">Close</span>
            <svg style={{ height: '20px', width: '20px', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{
              margin: '0 auto 16px',
              display: 'flex',
              height: '64px',
              width: '64px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: '#dbeafe',
            }}>
              <Icon className="h-8 w-8 text-blue-600" />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827' }}>{config.title}</h2>
            <p style={{ fontSize: '16px', color: '#4b5563', marginTop: '8px' }}>
              {config.description}
            </p>
          </div>

          {/* Details */}
          <div style={{
            margin: '16px 0',
            borderRadius: '8px',
            backgroundColor: '#f3f4f6',
            padding: '16px',
          }}>
            <div style={{
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#1f2937',
            }}>
              <Monitor style={{ height: '16px', width: '16px' }} />
              <span>Available on Web:</span>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {config.details.map((detail, index) => (
                <li key={index} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  fontSize: '14px',
                  color: '#4b5563',
                  marginBottom: '4px',
                }}>
                  <span style={{ color: '#2563eb' }}>•</span>
                  {detail}
                </li>
              ))}
            </ul>
          </div>

          {/* Footer.
              The primary button LINKS OUT to the web billing page in the
              SYSTEM browser. Legal on the US storefront since the May 2025
              Epic v. Apple contempt order (appeal failed, SCOTUS stay
              denied): external purchase links need no entitlement and pay no
              commission. The flow still never runs inside our webview. */}
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={() => { void openExternalCheckout(feature); onOpenChange(false); }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '15px',
                cursor: 'pointer',
              }}
            >
              Continue on app.insuracentral.com →
            </button>
            <button
              onClick={() => onOpenChange(false)}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                color: '#374151',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // Use portal to render modal at document.body level
  // This escapes any CSS stacking context issues from parent elements
  return createPortal(modalContent, document.body);
}

/**
 * Hook to use the WebVersionModal
 */
export function useWebVersionModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [feature, setFeature] = useState<WebVersionFeature>('subscription');

  const showModal = useCallback((featureType: WebVersionFeature) => {
    setFeature(featureType);
    setIsOpen(true);
  }, []);

  const hideModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    feature,
    showModal,
    hideModal,
    setIsOpen,
    Modal: () => (
      <WebVersionModal
        open={isOpen}
        onOpenChange={setIsOpen}
        feature={feature}
      />
    ),
  };
}

/**
 * Check if a feature requires web version and show modal if on mobile
 */
export function checkWebFeature(
  feature: WebVersionFeature,
  showModal: (feature: WebVersionFeature) => void,
  isNative: boolean,
): boolean {
  if (isNative) {
    showModal(feature);
    return false; // Return false to indicate action should not proceed
  }
  return true; // Return true to indicate action can proceed
}
